/**
 * Translates raw server/operation errors into human-friendly messages.
 * Never exposes technical details (stack traces, IDs, raw API responses) to users.
 */

/**
 * Returns a user-facing error message. Never returns the raw server error.
 * @param err - The caught error (unknown type)
 * @param fallback - Message to use when no specific mapping applies (default: generic)
 */
export function isLockContentionError(err: unknown): boolean {
  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!message || typeof message !== "string") return false;

  const lower = message.toLowerCase();
  return (
    lower.startsWith("concurrency_locked:") ||
    message.includes("already running") ||
    lower.includes("lock_busy")
  );
}

export function formatUserFacingError(
  err: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!message || typeof message !== "string") return fallback;

  const lower = message.toLowerCase();

  // Known technical prefixes → friendly messages
  if (isLockContentionError(err)) {
    return "Another sync operation is already running. Try again in a moment.";
  }
  if (lower.startsWith("out_of_date:")) {
    return "Your project is out of sync with the remote. Please sync first, then try again.";
  }
  const duplicateProjectedPathMatch = message.match(
    /duplicate projected path "([^"]+)"/i,
  );
  if (duplicateProjectedPathMatch) {
    return `Google Drive has duplicate file paths in the selected folder. Rename or remove the duplicate item at "${duplicateProjectedPathMatch[1]}", then sync again.`;
  }
  const duplicateDrivePathMessageMatch = message.match(
    /google drive has duplicate file paths.*duplicate item at "([^"]+)"/i,
  );
  if (duplicateDrivePathMessageMatch) {
    return `Google Drive has duplicate file paths in the selected folder. Rename or remove the duplicate item at "${duplicateDrivePathMessageMatch[1]}", then sync again.`;
  }
  if (lower.includes("google drive has duplicate file paths")) {
    return "Google Drive has duplicate file paths in the selected folder. Rename or remove the duplicate item, then sync again.";
  }
  if (
    lower.startsWith("integration_blocked:") ||
    lower.includes("integration blocked")
  ) {
    return "Remote integration is unavailable. Reconnect integration to continue.";
  }
  if (lower.startsWith("remote_unavailable:")) {
    return "This project does not support remote sync actions.";
  }
  if (lower.startsWith("billing_email_conflict:")) {
    return "This billing email is already used by another organization. Please set a different billing email and try again.";
  }
  if (lower.startsWith("billing_config_error:")) {
    return "Billing is temporarily unavailable. Please try again later.";
  }
  // Polar configuration / runtime failures should surface as a "billing config"
  // friendly message. We intentionally avoid exposing raw Polar responses.
  if (
    lower.includes("polar_access_token") ||
    lower.includes("polar_organization_id") ||
    lower.includes("polar api request failed") ||
    lower.includes("missing product configuration for")
  ) {
    return "Billing is temporarily unavailable. Please try again later.";
  }

  // Common server messages that are already user-friendly (allow through)
  if (lower.includes("unauthorized")) return "Please sign in again.";
  if (lower.includes("project not found")) return "Project not found.";
  if (lower.includes("viewer is read-only")) return "Viewer is read-only.";
  if (lower.includes("only owners can")) return message; // e.g. "Only owners can change integration mode."
  if (lower.includes("only project owners")) return message;
  if (lower.includes("conflict not found")) return "Conflict not found.";
  if (lower.includes("conversation not found"))
    return "Conversation not found.";
  if (lower.includes("you do not have access")) return message;
  if (lower.includes("invite not found")) return "Invite not found.";
  if (lower.includes("invite has expired")) return "Invite has expired.";
  if (lower.includes("invite is no longer valid"))
    return "Invite is no longer valid.";
  if (lower.includes("signed-in email does not match")) return message;

  // Technical patterns → never expose
  if (
    /correlationid=/i.test(message) ||
    /[a-f0-9]{32,}/i.test(message) || // long hex hashes
    message.includes("(correlationId=") ||
    /lock_busy:|scope:|expiresat/i.test(message)
  ) {
    return fallback;
  }

  // All other server messages: use fallback (never expose raw server text)
  return fallback;
}
